import * as React from 'react';
import { Result, SessionProvider, handleServerErrors } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { FlowAction, Permission, ProductForSaleDTO, Res, ScaleTicketDTO } from 'core/api';
import * as Images from 'pos/Images';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { OrderingFacade, RestaurantFacade } from 'pos/facades';
import * as SalesStore from '../SalesStore';
import { Col, Cols, Flex, Format } from 'core/ui';
import classNames from 'classnames';
import { Message, Modal } from 'core/ui-next/modal';

function getBarcode(ticket: ScaleTicketDTO): string {

	if (ticket.Scale.length !== 2)
		throw new Error('El número de balanza debe ser de 2 dígitos');

	if (ticket.Ticket.length !== 4)
		throw new Error('El número de ticket debe ser de 4 dígitos');

	return `2${ticket.Scale}00000${ticket.Ticket}`;
}

type Props = {
	tickets: ScaleTicketDTO[],
	compose: (product: ProductForSaleDTO) => Promise<Result>
} & Modal.Actions<void>;

type State = {
	tickets: ScaleTicketDTO[]
	selectedTicket: Nullable<ScaleTicketDTO>;
};

class ShowScaleTicketsScreen extends PosScreenClassComponent<Props, State> {

	private reloadTimer: Nullable<NodeJS.Timeout> = null;
	private enableReload = true;

	createInitialState(_props: Props): State {
		return { tickets: [], selectedTicket: null };
	}

	async onComponentDidMount(): Promise<void> {

		this.on('pos:sale:ticket-closed', async () => {
			// Si me cierran un ticket y es necesario aplicar algún flujo me tengo que cerrar.
			const action = SessionProvider.getAfterCloseTicketAction(SalesStore.getTicket().Location);
			if (action !== FlowAction.DoNothing)
				this.close();
		});

		await this.reloadTickets();
		this.reloadTimer = setInterval(this.reloadTickets, 5000);
	}

	onComponentWillUnmount(): void {
		if (this.reloadTimer)
			clearInterval(this.reloadTimer);
	}

	private pauseReload = async (action: () => Promise<void>) => {

		try {
			this.enableReload = false;
			await action();
		}
		finally {
			this.enableReload = true;
			await this.reloadTickets();
		}
	};

	protected getImage(): string | SvgIcon {
		return Images.ScaleTickets;
	}

	protected getSize(): ScreenSize {
		return { width: 1000, height: 700 };
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			{ image: Images.Reset, title: Res.Web.SHOW_SCALE_TICKET_TITLE(), action: this.reloadTickets },
			null,
			{ image: Images.CloseTicket, title: Res.Web.SHOW_SCALE_TICKET_CLOSE_TICKET_BTN(), action: async () => this.pauseReload(this.closeTicket.bind(this)) },
			{ image: Images.Add, title: Res.Web.SHOW_SCALE_TICKET_ADD_TO_TICKET_BTN(), action: async () => this.pauseReload(this.addToTicket.bind(this)) }
		];
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected getScreenId(): string {
		return 'SHOW_SCALE_TICKETS';
	}

	protected getTitle(): string {
		return Res.Web.SHOW_SCALE_TICKET_TITLE();
	}

	private reloadTickets = async () => {

		if (!this.enableReload)
			return;

		this.ifMounted(async () => {
			const tickets = await RestaurantFacade.getPendingScaleTickets(SalesStore.getTicket().Id);
			const selectedTicket = tickets.find(x => x.Ticket === this.state.selectedTicket?.Ticket) ?? null;
			this.setState({ tickets, selectedTicket });
		});
	};

	private closeCurrentTicket = async () => {

		if (SalesStore.ticketCanBeClosedAsRefund()) {
			const confirm = await Message.confirm(Res.Msg.MSG_EMIT_REFUND());
			return confirm.ok ? await Command.execute('closeCurrentTicket', { closeAs: 'Refund' }) : Result.error();
		}

		return await Command.execute('closeCurrentTicket', { closeAs: 'Invoice' });
	};

	async addToTicket(): Promise<void> {

		if (this.state.selectedTicket === null)
			return;

		const previousTicket = SalesStore.getTicket();
		await SalesStore.createNewTicket(previousTicket.Location);

		try {

			const result = await Command.execute('sellByBarcode', getBarcode(this.state.selectedTicket), this.props.compose);

			if (!result.ok) {
				await SalesStore.releaseScaleTicket(this.state.selectedTicket);
				return;
			}

			const joinResult = await handleServerErrors(() => OrderingFacade.joinTicket(SalesStore.getTicket(), true, previousTicket.Location, previousTicket.Id), {
				onEmptyTicketError: async _error => {
					await Message.show(Res.Msg.MSG_TICKET_HAS_BEEN_MODIFIED_AND_NOW_IT_IS_EMPTY());
				},
				onDomainError: async error => {
					await Message.show(error.message);
				},
				onTicketNotFoundError: async _error => {
					await Message.show(Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER());
				}
			});
			if (!joinResult.ok) {
				await SalesStore.releaseScaleTicket(this.state.selectedTicket);
				return;

			}

			this.close();
		}
		finally {

			// Trato de limpiar el ticket nuevo que se crea después de cobrar un ticket
			if (previousTicket.Id !== SalesStore.getTicket().Id) {
				await SalesStore.release();
				await SalesStore.loadTicketById(previousTicket.Id);
			}
		}

	}

	async closeTicket(): Promise<void> {

		if (this.state.selectedTicket === null)
			return;

		const previousTicket = SalesStore.getTicket();
		await SalesStore.createNewTicket(previousTicket.Location);

		try {
			const result = await Command.execute('sellByBarcode', getBarcode(this.state.selectedTicket), this.props.compose);
			if (!result.ok) {
				await SalesStore.releaseScaleTicket(this.state.selectedTicket);
				return;
			}

			const closeResult = await this.closeCurrentTicket();
			if (!closeResult.ok) {
				await SalesStore.releaseScaleTicket(this.state.selectedTicket);
				return;
			}
		}
		finally {

			// Trato de limpiar el ticket nuevo que se crea después de cobrar un ticket
			if (previousTicket.Id !== SalesStore.getTicket().Id) {
				await SalesStore.release();
				await SalesStore.loadTicketById(previousTicket.Id);
			}
		}
	}

	selectTicket = (ticket: ScaleTicketDTO): void => {
		this.setState({ selectedTicket: ticket });
	};

	protected getContent(): React.ReactNode {

		return <Flex.Horizontal className='p-t'>
			<Flex.Item size='*' className='p-r p-l'>
				<TicketList tickets={this.state.tickets} selectedTicket={this.state.selectedTicket} onSelectTicket={this.selectTicket} />
			</Flex.Item>
			<Flex.Item size='*' className='p-r p-l'>
				<TicketLines selectedTicket={this.state.selectedTicket} />
			</Flex.Item>
		</Flex.Horizontal>;

	}
}

function TicketList({ tickets, selectedTicket, onSelectTicket }: { tickets: ScaleTicketDTO[], selectedTicket: Nullable<ScaleTicketDTO>, onSelectTicket: (newSelectedTicket: ScaleTicketDTO) => void }): React.ReactNode {
	return <Flex.Vertical>
		<Flex.Item className='text-26 text-uppercase text-center p-xs'>
			{Res.Web.SHOW_SCALE_TICKET_TICKETS_GRP()}
		</Flex.Item>
		<Flex.Item size='*'>
			<Flex.Vertical>
				<Flex.Item>
					<Cols className='list-header'>
						<Col width='8ex' className='text-left'>{Res.Web.SHOW_SCALE_TICKET_TIME_CLM()}</Col>
						<Col width='12ex' className='text-left'>{Res.Web.SHOW_SCALE_TICKET_USER_CLM()}</Col>
						<Col width='12ex' className='text-left'>{Res.Web.SHOW_SCALE_TICKET_SCALE_CLM()}</Col>
						<Col width='10ex' className='text-left'>{Res.Web.SHOW_SCALE_TICKET_TICKET_CLM()}</Col>
						<Col width='7ex' className='text-left'>{Res.Web.SHOW_SCALE_TICKET_LINES_CLM()}</Col>
						<Col width='*' className='text-center'>{Res.Web.SHOW_SCALE_TICKET_TOTAL_CLM()}</Col>
					</Cols>
				</Flex.Item>
				<Flex.Item size='*'>
					<ul className='list'>
						{tickets.map(ticket => {
							const className = classNames({
								selected: selectedTicket?.Ticket === ticket.Ticket
							});

							return <li key={ticket.Ticket} className={className} onClick={() => onSelectTicket(ticket)}>
								<Cols>
									<Col width='8ex' className='text-left'>{Format.asTime(ticket.Date)}</Col>
									<Col width='12ex' className='text-left'>{ticket.User}</Col>
									<Col width='12ex' className='text-left'>{ticket.Scale}</Col>
									<Col width='10ex' className='text-left'>{ticket.Ticket}</Col>
									<Col width='7ex' className='text-right'>{ticket.ItemsCount}</Col>
									<Col width='*' className='text-right'>{Format.asMoney(ticket.Total)}</Col>
								</Cols>
							</li>;
						})}
					</ul>
				</Flex.Item>
			</Flex.Vertical>
		</Flex.Item>
	</Flex.Vertical>;
}

function TicketLines({ selectedTicket }: { selectedTicket: Nullable<ScaleTicketDTO> }): React.ReactNode {

	return <Flex.Vertical>
		<Flex.Item className='text-26 text-uppercase text-center p-xs'>
			{Res.Web.SHOW_SCASLE_TICKET_DETAILS_GRP()}
		</Flex.Item>
		<Flex.Item size='*'>
			<Flex.Vertical>
				<Flex.Item>
					<Cols className='list-header'>
						<Col width='*' className='text-left'>{Res.Web.SHOW_SCALE_TICKET_PRODUCT_CLM()}</Col>
						<Col width='9ex' className='text-left'>{Res.Web.SHOW_SCALE_TICKET_QUANTITY_CLM()}</Col>
						<Col width='13ex' className='text-center'>{Res.Web.SHOW_SCALE_TICKET_UNIT_PRICE_CLM()}</Col>
						<Col width='*' className='text-center'>{Res.Web.SHOW_SCALE_TICKET_TOTAL_CLM()}</Col>
					</Cols>
				</Flex.Item>
				<Flex.Item size='*'>
					{selectedTicket
						? <ul className='list'>
							{selectedTicket.Items.map((item, index) =>
								<li key={index}>
									<Cols>
										<Col width='*'>{item.Product}</Col>
										<Col width='9ex' className='text-right'>{Format.asQuantity(item.Quantity)}</Col>
										<Col width='13ex' className='text-right'>{Format.asMoney(item.Price)}</Col>
										<Col width='*' className='text-right'>{Format.asMoney(item.Total)}</Col>
									</Cols>
								</li>
							)}
						</ul>
						: <p className='text-26 text-muted p-t text-center'>{Res.Web.SHOW_SCALE_TICKET_NO_TICKET_SELECTED()}</p>
					}
				</Flex.Item>
			</Flex.Vertical>
		</Flex.Item>
	</Flex.Vertical>;
}

declare global {
	interface CommandType {
		showScaleTickets: (compose: (product: ProductForSaleDTO) => Promise<Result>) => Promise<Result>;
	}
}


Command.register('showScaleTickets', Preconditions.and(Preconditions.ensure(Permission.ScaleTicket_ShowList), Preconditions.ensureIsNotLite), async (compose: (product: ProductForSaleDTO) => Promise<Result>): Promise<Result> => {
	const tickets = await RestaurantFacade.getPendingScaleTickets(SalesStore.getTicket().Id);
	return await Modal.show(ShowScaleTicketsScreen, { tickets, compose });
});
